import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import retreatService from '../services/retreatService';

const RetreatSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');

  const [retreats, setRetreats] = useState([]);
  const [selectedRetreat, setSelectedRetreat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (centerId) {
      fetchRetreats();
    }
  }, [centerId]);

  const fetchRetreats = async () => {
    try {
      setLoading(true);
      const data = await retreatService.getRetreatsByCenter(centerId);
      setRetreats(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load retreats. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRetreat = (retreat) => {
    setSelectedRetreat(retreat);
  };

  const handleProceedToRegistration = () => {
    if (selectedRetreat) {
      navigate(`/retreat/registration?retreatId=${selectedRetreat.retreatId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <div className="mb-12">
          <button
            onClick={() => navigate('/retreat/center')}
            className="text-blue-600 hover:text-blue-800 mb-4 font-semibold"
          >
            ← Back to Centers
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Select Retreat</h1>
          <p className="text-lg text-gray-600">
            Choose a retreat and session to register
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading retreats...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && retreats.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {retreats.map((retreat) => (
                  <div
                    key={retreat.retreatId}
                    onClick={() => handleSelectRetreat(retreat)}
                    className={`item-card cursor-pointer ${
                      selectedRetreat?.retreatId === retreat.retreatId
                        ? 'selected'
                        : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {retreat.retreatName}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          Category: {retreat.category}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-blue-600">
                          ${retreat.retreatFee}
                        </p>
                        <p className="text-gray-600 text-sm">per person</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">
                      {retreat.retreatDescription}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Start Date</p>
                        <p className="font-semibold">
                          {new Date(retreat.retreatStartDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">End Date</p>
                        <p className="font-semibold">
                          {new Date(retreat.retreatEndDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Accommodation</p>
                        <p className="font-semibold">{retreat.accommodationType}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Available Spots</p>
                        <p className="font-semibold">
                          {retreat.capacity - retreat.registeredCount}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              {selectedRetreat ? (
                <div className="card sticky top-4">
                  <h3 className="text-xl font-bold mb-4">Retreat Summary</h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-gray-600 text-sm">Retreat Name</p>
                      <p className="font-semibold">{selectedRetreat.retreatName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Category</p>
                      <p className="font-semibold">{selectedRetreat.category}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Duration</p>
                      <p className="font-semibold">
                        {new Date(selectedRetreat.retreatStartDate).toLocaleDateString()} -{' '}
                        {new Date(selectedRetreat.retreatEndDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-gray-600 text-sm">Total Fee</p>
                      <p className="text-3xl font-bold text-blue-600">
                        ${selectedRetreat.retreatFee}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleProceedToRegistration}
                    className="btn-primary w-full"
                  >
                    Proceed to Registration
                  </button>
                </div>
              ) : (
                <div className="card text-center text-gray-500">
                  <p>Select a retreat to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && retreats.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No retreats available for this center.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetreatSelection;
